import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface InvoiceEditRequest {
  _id: string;
  documentType: 'direct_sale' | 'tax_invoice' | 'sales_order';
  documentId: string;
  documentNumber: string;
  customerName?: string;
  previousGrandTotal: number;
  proposedGrandTotal: number;
  previousLines: Array<Record<string, unknown>>;
  computedResult: {
    lines?: Array<Record<string, unknown>>;
    lineItems?: Array<Record<string, unknown>>;
    /** Set on a Dispatch hand-off edit — no line or price change. */
    dispatchHandoff?: {
      paymentArrangement?: string; transportMode?: string; courierName?: string; driverName?: string;
      note?: string; requiredDispatchDate?: string | null; creditAmount?: number;
      paymentProofs?: { url: string; name: string }[];
    };
    /** Hand-off edit: the payment-proof documents on file when it was requested. */
    previousPaymentProofs?: { url: string; name: string }[];
    /** Tax-invoice edit: header fields that change (Bill To, billing currency). */
    patch?: {
      billToName?: string; billToGstin?: string;
      billingAddress?: { street?: string; city?: string; state?: string; zipCode?: string; country?: string };
      foreignCurrency?: string; foreignRate?: number;
    };
  };
  reason: string;
  bdmReason?: string;
  revisionCount?: number;
  status: 'pending' | 'approved' | 'rejected';
  requestedBy: string;
  requestedByName: string;
  requestedAt: string;
  reviewedByName?: string;
  reviewedAt?: string | null;
  reviewNote?: string;
}

export function useInvoiceEditRequests(status?: string) {
  return useQuery({
    queryKey: ['invoice-edit-requests', status ?? 'all'],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const { data } = await api.get<{ items: InvoiceEditRequest[] }>(`/api/invoice-edit-requests?${params}`);
      return data.items ?? [];
    },
  });
}

export function useInvoiceEditRequestActions() {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['invoice-edit-requests'] }),
    qc.invalidateQueries({ queryKey: ['invoices-direct'] }),
    qc.invalidateQueries({ queryKey: ['invoices'] }),
  ]);

  const approve = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) => {
      const { data } = await api.post(`/api/invoice-edit-requests/${id}/approve`, { note });
      return data;
    },
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) => {
      const { data } = await api.post(`/api/invoice-edit-requests/${id}/reject`, { note });
      return data;
    },
    onSuccess: invalidate,
  });

  return { approve, reject };
}
